import { useState } from "react";
import { AudioWaveform, Volume2, Mic, ChevronDown, ChevronUp, RotateCcw } from "lucide-react";
import { useAria } from "../context/AriaContext";
import { ARIA_COMMANDS, DEFAULT_SETTINGS } from "../lib/aria";

export default function AriaSettings() {
  const aria = useAria();
  const [showCommands, setShowCommands] = useState(false);
  const s = aria.settings;

  const setAccent = (accent) => aria.updateSettings({ accent });
  const setRate = (rate) => aria.updateSettings({ rate });
  const toggle = (k) => aria.updateSettings({ [k]: !s[k] });
  const reset = () => aria.updateSettings({ ...DEFAULT_SETTINGS });

  const testVoice = async () => {
    await aria.speak("This is ARIA, your ProsthoAI clinical assistant. I will guide you through your case analysis with clinical precision.");
  };

  return (
    <div className="bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-lg p-5" data-testid="aria-settings">
      <div className="flex items-center gap-2 mb-1">
        <AudioWaveform size={16} className="text-[var(--pa-gold)]" />
        <h3 className="font-serif text-lg text-[var(--pa-text)]">ARIA — Clinical Voice Assistant</h3>
      </div>
      <p className="text-xs text-[var(--pa-text-muted)] mb-4">
        Precise, warm clinical voice. Speaks Australian English by default, with a British fallback for browsers that lack en-AU voices.
      </p>

      {/* Enable toggle */}
      <div className="space-y-3">
        <label className="flex items-center justify-between gap-3 cursor-pointer">
          <div>
            <div className="text-sm text-[var(--pa-text)]">ARIA enabled</div>
            <div className="text-[11px] text-[var(--pa-text-dim)]">Master switch — turns off voice + listening across the platform.</div>
          </div>
          <input
            type="checkbox"
            checked={s.enabled}
            onChange={() => toggle("enabled")}
            data-testid="aria-toggle-enabled"
            className="w-4 h-4 accent-[var(--pa-gold)]"
          />
        </label>

        <label className="flex items-center justify-between gap-3 cursor-pointer">
          <div>
            <div className="text-sm text-[var(--pa-text)]">ARIA spoken responses</div>
            <div className="text-[11px] text-[var(--pa-text-dim)]">Rachel voice on/off. Text answers and microphone input keep working when off.</div>
          </div>
          <input
            type="checkbox"
            checked={s.spokenResponses !== false}
            onChange={() => toggle("spokenResponses")}
            data-testid="aria-toggle-spoken-responses"
            className="w-4 h-4 accent-[var(--pa-gold)]"
          />
        </label>

        <label className="flex items-center justify-between gap-3 cursor-pointer">
          <div>
            <div className="text-sm text-[var(--pa-text)]">Red flag alerts</div>
            <div className="text-[11px] text-[var(--pa-text-dim)]">Speak immediately when topology analysis flags a clinical concern.</div>
          </div>
          <input
            type="checkbox"
            checked={s.redFlagAlerts}
            onChange={() => toggle("redFlagAlerts")}
            data-testid="aria-toggle-red-flags"
            className="w-4 h-4 accent-[var(--pa-gold)]"
          />
        </label>

        <label className="flex items-center justify-between gap-3 cursor-pointer">
          <div>
            <div className="text-sm text-[var(--pa-text)]">Topology narration</div>
            <div className="text-[11px] text-[var(--pa-text-dim)]">Narrate the agent topology as it analyses your case.</div>
          </div>
          <input
            type="checkbox"
            checked={s.topologyNarration}
            onChange={() => toggle("topologyNarration")}
            data-testid="aria-toggle-topology"
            className="w-4 h-4 accent-[var(--pa-gold)]"
          />
        </label>

        <label className="flex items-center justify-between gap-3 cursor-pointer">
          <div>
            <div className="text-sm text-[var(--pa-text)]">Module greetings</div>
            <div className="text-[11px] text-[var(--pa-text-dim)]">Brief clinical orientation when you open a clinical module.</div>
          </div>
          <input
            type="checkbox"
            checked={s.moduleGreetings}
            onChange={() => toggle("moduleGreetings")}
            data-testid="aria-toggle-module-greetings"
            className="w-4 h-4 accent-[var(--pa-gold)]"
          />
        </label>

        <label className="flex items-center justify-between gap-3 cursor-pointer">
          <div>
            <div className="text-sm text-[var(--pa-text)]">Daily huddle briefing</div>
            <div className="text-[11px] text-[var(--pa-text-dim)]">On login, ARIA reads a short briefing — red flags, running analyses, review queue.</div>
          </div>
          <input
            type="checkbox"
            checked={s.huddleAtLogin}
            onChange={() => toggle("huddleAtLogin")}
            data-testid="aria-toggle-huddle"
            className="w-4 h-4 accent-[var(--pa-gold)]"
          />
        </label>
      </div>

      {/* Accent */}
      <div className="mt-5">
        <div className="kicker mb-2">Accent</div>
        <div className="flex gap-2" data-testid="aria-accent-group">
          {[
            { v: "en-AU", l: "Australian" },
            { v: "en-GB", l: "British" },
          ].map((opt) => (
            <button
              key={opt.v}
              onClick={() => setAccent(opt.v)}
              data-testid={`aria-accent-${opt.v}`}
              className={`px-3 py-1.5 rounded-md text-xs transition border ${
                s.accent === opt.v
                  ? "bg-[var(--pa-gold)] text-[var(--pa-bg)] border-[var(--pa-gold)]"
                  : "bg-[var(--pa-bg-2)] text-[var(--pa-text-muted)] border-[var(--pa-border)] hover:text-[var(--pa-text)]"
              }`}
            >
              {opt.l}
            </button>
          ))}
        </div>
        <div className="mt-1 text-[10px] text-[var(--pa-text-dim)]">
          Selected voice: <span className="font-mono text-[var(--pa-gold)]">{aria.voice?.name || "auto"}</span>
        </div>
      </div>

      {/* Rate */}
      <div className="mt-5">
        <div className="kicker mb-2 flex justify-between">
          <span>Speaking rate</span>
          <span className="text-[var(--pa-gold)] font-mono">{s.rate.toFixed(2)}×</span>
        </div>
        <input
          type="range"
          min="0.7"
          max="1.3"
          step="0.02"
          value={s.rate}
          onChange={(e) => setRate(parseFloat(e.target.value))}
          data-testid="aria-rate"
          className="w-full accent-[var(--pa-gold)]"
        />
        <div className="flex justify-between text-[10px] text-[var(--pa-text-dim)] mt-1">
          <span>Slower</span><span>Default 0.92</span><span>Faster</span>
        </div>
      </div>

      {/* Test + reset */}
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          onClick={testVoice}
          disabled={!s.enabled}
          data-testid="aria-test-voice"
          className="inline-flex items-center gap-2 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold rounded-md px-4 py-2 hover:bg-[var(--pa-gold-hover)] transition text-sm disabled:opacity-50"
        >
          <Volume2 size={14} /> Test voice
        </button>
        <button
          onClick={() => aria.greet()}
          disabled={!s.enabled}
          data-testid="aria-test-greet"
          className="inline-flex items-center gap-2 border border-[var(--pa-gold)] text-[var(--pa-gold)] rounded-md px-4 py-2 hover:bg-[var(--pa-gold)]/10 transition text-sm disabled:opacity-50"
        >
          <Mic size={14} /> Greet me
        </button>
        <button
          onClick={reset}
          data-testid="aria-reset"
          className="inline-flex items-center gap-1 text-xs text-[var(--pa-text-muted)] hover:text-[var(--pa-gold)]"
        >
          <RotateCcw size={12} /> Reset to defaults
        </button>
      </div>

      {/* Commands accordion */}
      <div className="mt-6 border-t border-[var(--pa-border)] pt-4">
        <button
          onClick={() => setShowCommands((x) => !x)}
          data-testid="aria-toggle-commands"
          className="w-full flex items-center justify-between text-sm text-[var(--pa-text)] hover:text-[var(--pa-gold)] transition"
        >
          <span className="flex items-center gap-2">
            <AudioWaveform size={14} className="text-[var(--pa-gold)]" />
            All ARIA voice commands ({ARIA_COMMANDS.length})
          </span>
          {showCommands ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        {showCommands && (
          <ul className="mt-3 grid gap-1.5 text-xs" data-testid="aria-commands-list">
            {ARIA_COMMANDS.map((c) => (
              <li key={c.label} className="flex items-start justify-between gap-3 py-1">
                <div className="text-[var(--pa-text-muted)]">
                  <span className="text-[var(--pa-gold)]">"{c.patterns[0]}"</span>{c.patterns.length > 1 && (
                    <span className="text-[var(--pa-text-dim)]"> · or {c.patterns.slice(1).map((p) => `"${p}"`).join(", ")}</span>
                  )}
                </div>
                <div className="text-[var(--pa-text)] text-right shrink-0">
                  {c.label}{c.adminOnly && <span className="ml-1 text-[9px] text-amber-400">ADMIN</span>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {!aria.speechAvailable && (
        <div className="mt-4 text-[11px] text-amber-400">
          Your browser does not support speech synthesis. ARIA will be silent.
        </div>
      )}
      {!aria.recognitionAvailable && (
        <div className="mt-2 text-[11px] text-[var(--pa-text-dim)]">
          Voice input requires Chrome, Edge, or Safari. ARIA will still speak, but cannot hear commands in this browser.
        </div>
      )}
    </div>
  );
}
